import { attr, Constructable, ElementStyles, FASTElement, HostBehavior, HostController, Observable } from '@microsoft/fast-element';
import { designTokens } from '../design-tokens';
import { DI, inject, Registration } from '@microsoft/fast-element/di.js';
import { PropertyStyleSheetBehavior } from '../foundation/property-stylesheet-behavior';

export const mediaSizes = ['extra-small', 'small', 'medium', 'large', 'extra-large'] as const;
export type MediaSize = typeof mediaSizes[number];

const container = DI.getOrCreateDOMContainer();

container.register(Registration.instance('mediaSizeObserver',
    new ResizeObserver((entries) => {
        for(const entry of entries) {
            if(entry.borderBoxSize) {
                var width = entry.borderBoxSize[0].inlineSize;

                if(width > designTokens['space-breakpoint-extra-large'].getValueFor(entry.target as FASTElement))
                    entry.target.setAttribute('media-size', 'extra-large');
                else if (width > designTokens['space-breakpoint-large'].getValueFor(entry.target as FASTElement))
                    entry.target.setAttribute('media-size', 'large');
                else if (width > designTokens['space-breakpoint-medium'].getValueFor(entry.target as FASTElement))
                    entry.target.setAttribute('media-size', 'medium');
                else if (width > designTokens['space-breakpoint-small'].getValueFor(entry.target as FASTElement))
                    entry.target.setAttribute('media-size', 'small');
                else
                    entry.target.setAttribute('media-size', 'extra-small');
            }
        }
    })
));

container.register(Registration.instance('documentMediaSizeObserver',
    (() =>{
        let setMediaSize = (width, element) => {
            if(width > designTokens['space-breakpoint-extra-large'].getValueFor(element))
                element.setAttribute('document-media-size', 'extra-large');
            else if (width > designTokens['space-breakpoint-large'].getValueFor(element))
                element.setAttribute('document-media-size', 'large');
            else if (width > designTokens['space-breakpoint-medium'].getValueFor(element))
                element.setAttribute('document-media-size', 'medium');
            else if (width > designTokens['space-breakpoint-small'].getValueFor(element))
                element.setAttribute('document-media-size', 'small');
            else
            element.setAttribute('document-media-size', 'extra-small');
        };

        let targets = new Set<FASTElement>;
        let documentResizeObserver = new ResizeObserver((entries) => {
            var width = document.body.clientWidth;

            for(const target of targets) {
                setMediaSize(width, target);
            }
        })

        documentResizeObserver.observe(document.body);

        return {
            register: (target: FASTElement) => {
                targets.add(target);
                setMediaSize(document.body.clientWidth, target);
            },
            unregister: (target: FASTElement) => targets.delete(target)
        }
    })()
));

export function mediaSizeBehavior(value: string, styles: ElementStyles, negate: boolean = false) {
    return new PropertyStyleSheetBehavior('mediaSize', value, styles, negate);
}

export function documentMediaSizeBehavior(value: string, styles: ElementStyles, negate: boolean = false) {
    return new PropertyStyleSheetBehavior('documentMediaSize', value, styles, negate);
}

export function MediaSizeMixin<TBase extends Constructable<FASTElement>>(Base: TBase) {
    class mediaSizeMixin extends Base {
        @inject('mediaSizeObserver')
        protected mediaSizeObserver!: ResizeObserver;

        /** A readonly attribute which reflects the closest breakpoint size of this container. */
        @attr({ attribute: 'media-size' })
        mediaSize: MediaSize;

        connectedCallback() {
            super.connectedCallback();
            this.mediaSizeObserver.observe(this);
        }

        disconnectedCallback() {
            super.disconnectedCallback();
            this.mediaSizeObserver.unobserve(this);
        }
    }

    return mediaSizeMixin;
}

export function DocumentMediaSizeMixin<TBase extends Constructable<FASTElement>>(Base: TBase) {
    class documentMediaSizeMixin extends Base {
        @inject('documentMediaSizeObserver')
        protected documentMediaSizeObserver!: any;

        /** A readonly attribute which reflects the closest breakpoint size of this container. */
        @attr({ attribute: 'document-media-size' })
        documentMediaSize: MediaSize;

        connectedCallback() {
            super.connectedCallback();
            console.log('document media size mixin connected');
            this.documentMediaSizeObserver.register(this);
        }

        disconnectedCallback() {
            super.disconnectedCallback();
            this.documentMediaSizeObserver.unregister(this);
        }
    }

    return documentMediaSizeMixin;
}

export class RelativeMediaSizeStyleSheetBehavior implements HostBehavior {
    constructor(
        private propertyName: string,
        private value: MediaSize,
        private styles: ElementStyles,
        private negate: boolean = false,
    ) {}

    public addedCallback(controller: HostController) {
        Observable.getNotifier(controller.source).subscribe(this, this.propertyName);
        this.handleChange(controller.source, this.propertyName);
    }

    public removedCallback(controller: HostController) {
        Observable.getNotifier(controller.source).unsubscribe(this, this.propertyName);
        controller.removeStyles(this.styles);
    }

    public handleChange(source: FASTElement, key: string) {
        const controller = source.$fastController;

        if ((mediaSizes.indexOf(source[key]) >= mediaSizes.indexOf(this.value)) !== this.negate)
            controller.addStyles(this.styles);
        else
            controller.removeStyles(this.styles);
    }
}

export function relativeMediaSizeBehavior(property: string, value: MediaSize, styles: ElementStyles, negate: boolean = false) {
    return new RelativeMediaSizeStyleSheetBehavior(property, value, styles);
}
